import { useAuth } from "@repo/auth";
import { useTranslation } from "@repo/i18n";
import { css } from "@repo/styled-system/css";
import { PageScaffold } from "../../shell/PageScaffold";
import { useVenue } from "../../shell/VenueContext";
import { canAccessVenue } from "../../shell/roles";
import { keyLabel, type AbuseBlock } from "./abuseApi";
import { useAbuseBlocks, useClearAbuseBlock } from "./queries";

const cell = css({ padding: "2", textAlign: "left", verticalAlign: "top" });
const table = css({ width: "full", borderCollapse: "collapse", fontSize: "sm" });

/**
 * The `/v/:venueId/abuse-blocks` MANAGER surface (booking-abuse-protection, R4/R7). Lists the venue's
 * active TEMPORARY blocks with their triggering-signal snapshot so staff can inspect a wrongly-flagged
 * diner, and clears one — the next submission then passes and the clear is audited (CP-6). Venue-scoped
 * + RLS: a manager never sees another tenant's blocks. A block is never permanent (that is exclusively a
 * blacklist decision) — it lapses on its own; this panel is the "wrongly-flagged diner" escape hatch.
 */
export function AbuseBlocksPanel() {
  const { venueId } = useVenue();
  const { claims } = useAuth();
  const { t } = useTranslation();
  const canManage = canAccessVenue(claims, venueId, "MANAGER");

  const blocks = useAbuseBlocks(venueId);
  const clear = useClearAbuseBlock(venueId);

  if (!canManage) {
    return (
      <PageScaffold title={t("abuse.blocks.title")}>
        <p>{t("abuse.blocks.forbidden")}</p>
      </PageScaffold>
    );
  }

  const items = blocks.data?.items ?? [];

  return (
    <PageScaffold title={t("abuse.blocks.title")}>
      <p className={css({ color: "fg.muted", marginBottom: "3" })}>{t("abuse.blocks.intro")}</p>

      {blocks.isLoading ? (
        <p>{t("abuse.blocks.loading")}</p>
      ) : items.length === 0 ? (
        <p data-testid="abuse-empty">{t("abuse.blocks.empty")}</p>
      ) : (
        <div className={css({ overflowX: "auto", width: "full" })}>
          <table className={table}>
            <thead>
              <tr>
                <th className={cell}>{t("abuse.blocks.col.key")}</th>
                <th className={cell}>{t("abuse.blocks.col.surface")}</th>
                <th className={cell}>{t("abuse.blocks.col.reason")}</th>
                <th className={cell}>{t("abuse.blocks.col.expires")}</th>
                <th className={cell}>{t("abuse.blocks.col.signal")}</th>
                <th className={cell} aria-label={t("abuse.blocks.col.actions")} />
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id}>
                  <td className={cell}>{keyLabel(b)}</td>
                  <td className={cell}>
                    {t(`abuse.blocks.surface.${b.surface}`, { defaultValue: b.surface })}
                  </td>
                  <td className={cell}>
                    {t(`abuse.blocks.reason.${b.reasonCode}`, { defaultValue: b.reasonCode })}
                  </td>
                  <td className={cell}>{new Date(b.expiresAtSec * 1000).toLocaleString()}</td>
                  <td className={cell}>
                    {/* Raw counters/flags are engineer-facing — tuck them behind a native disclosure so the
                      table reads in plain language, but staff can still expand the exact triggering signal. */}
                    <details>
                      <summary className={css({ cursor: "pointer", color: "fg.muted" })}>
                        {t("abuse.blocks.signalToggle")}
                      </summary>
                      <code className={css({ fontSize: "xs" })}>{signalSummary(b)}</code>
                    </details>
                  </td>
                  <td className={cell}>
                    <button
                      type="button"
                      disabled={clear.isPending}
                      onClick={() => clear.mutate(b.id)}
                      className={css({
                        px: "3",
                        py: "2",
                        minH: "44px",
                        whiteSpace: "nowrap",
                        borderWidth: "1px",
                        borderColor: "border.default",
                        rounded: "md",
                        cursor: "pointer",
                        _hover: { bg: "bg.subtle" },
                        _disabled: { opacity: 0.5, cursor: "not-allowed" },
                      })}
                    >
                      {t("abuse.blocks.clear")}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </PageScaffold>
  );
}

/** A compact one-line rendering of the triggering-signal snapshot for the staff table. */
function signalSummary(b: AbuseBlock): string {
  const s = b.signal;
  const parts: string[] = [];
  if (typeof s.riskScore === "number") parts.push(`score=${s.riskScore}`);
  if (typeof s.ipWindowCount === "number")
    parts.push(`ip=${s.ipWindowCount}/${s.ipWindowLimit ?? "?"}`);
  if (typeof s.phoneWindowCount === "number")
    parts.push(`phone=${s.phoneWindowCount}/${s.phoneWindowLimit ?? "?"}`);
  if (s.honeypotFilled === true) parts.push("honeypot");
  if (typeof s.formFillMillis === "number") parts.push(`fill=${s.formFillMillis}ms`);
  return parts.join(" ") || "—";
}
